import { HOST } from "@components/Constants";
import { type OrganizationPublicKeyAudience } from "@api/types";

import { AUDIENCE_BACKSTAGE } from "./audiences";

const PLACEHOLDER_SUBJECT = "<investor-id-in-your-system>";
const PLACEHOLDER_EMAIL = "<investor-email>";

const COMMENT_JTI = "// A different value for each token.";
const COMMENT_SUBJECT = "// The ID of that person in your own system.";
const COMMENT_EMAIL = "// The address of the investor account.";

const COMMENT_BACKSTAGE = "// The key names the user, therefore the token carries the jti claim only.";
const COMMENT_INVESTMENTS = "// The token signs in one of your investors.";

const CLAIMS_BACKSTAGE = `    jti: randomUUID(), ${COMMENT_JTI}`;

const CLAIMS_INVESTMENTS = `    jti: randomUUID(), ${COMMENT_JTI}
    sub: "${PLACEHOLDER_SUBJECT}", ${COMMENT_SUBJECT}
    email: "${PLACEHOLDER_EMAIL}", ${COMMENT_EMAIL}`;

type Props = {
  domain?: string;
  issuer: string;
  audience: OrganizationPublicKeyAudience;
  publicKeyId: string;
};

type SnippetProps = {
  host: string;
  issuer: string;
  publicKeyId: string;
};

type SignSnippetProps = {
  issuer: string;
  claims: string;
  comment: string;
  publicKeyId: string;
};

// NOTE: the client calls the API on the domain of the organization. An
//       organization with no domain of its own uses the host of this app.
const getHost = (domain?: string): string => {
  const hasDomain = !!domain;

  if (!hasDomain) {
    return HOST;
  }

  return `https://${domain}`;
};

const getSignSnippet = ({
  issuer,
  claims,
  comment,
  publicKeyId,
}: SignSnippetProps): string => {
  const snippet = `import { randomUUID } from "crypto";
import jwt from "jsonwebtoken";

// Your private key that pairs with this public key. Keep it secret.
const privateKey = process.env.FUNDAMENTAL_PRIVATE_KEY;

${comment}
const idToken = jwt.sign(
  {
${claims}
  },
  privateKey,
  {
    algorithm: "RS256",
    issuer: "${issuer}",
    keyid: "${publicKeyId}",
    expiresIn: "5m",
  },
);
`;

  return snippet;
};

const getAuthenticateSnippet = (host: string): string => {
  const snippet = `const HOST = "${host}";

// Step 1: exchange the ID token for the authentication token.
const authenticateResponse = await fetch(
  \`\${HOST}/api/identity/AuthenticateByPublicKey\`,
  {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ idToken }),
  },
);

const { data: identity } = await authenticateResponse.json();
const { authenticationJwt } = identity;
`;

  return snippet;
};

const getBackstageSnippet = ({
  host,
  issuer,
  publicKeyId,
}: SnippetProps): string => {
  const signSnippet = getSignSnippet({
    issuer,
    publicKeyId,
    claims: CLAIMS_BACKSTAGE,
    comment: COMMENT_BACKSTAGE,
  });

  const authenticateSnippet = getAuthenticateSnippet(host);

  const snippet = `${signSnippet}
${authenticateSnippet}
// Step 2: exchange the authentication token for the authorization token.
const authorizeResponse = await fetch(\`\${HOST}/api/backstage/Authorize\`, {
  method: "PATCH",
  headers: { authentication: authenticationJwt },
});

const { data: user } = await authorizeResponse.json();
const { authorizationJwt } = user;

// Step 3: put the authorization token into the authorization header.
const response = await fetch(\`\${HOST}/api/backstage/IndexUsers\`, {
  method: "GET",
  headers: { authorization: authorizationJwt },
});
`;

  return snippet;
};

const getInvestmentsSnippet = ({
  host,
  issuer,
  publicKeyId,
}: SnippetProps): string => {
  const signSnippet = getSignSnippet({
    issuer,
    publicKeyId,
    claims: CLAIMS_INVESTMENTS,
    comment: COMMENT_INVESTMENTS,
  });

  const authenticateSnippet = getAuthenticateSnippet(host);

  const snippet = `${signSnippet}
${authenticateSnippet}
// Step 2: exchange the authentication token for the authorization token.
const authorizeResponse = await fetch(\`\${HOST}/api/investments/Authorize\`, {
  method: "PATCH",
  headers: { authentication: authenticationJwt },
});

const { data: account } = await authorizeResponse.json();
const { authorizationJwt } = account;

// Step 3: put the authorization token into the authorization header.
const response = await fetch(\`\${HOST}/api/investments/ReadMyAccount\`, {
  method: "GET",
  headers: { authorization: authorizationJwt },
});
`;

  return snippet;
};

const getIdTokenSnippet = ({
  domain,
  issuer,
  audience,
  publicKeyId,
}: Props): string => {
  const host = getHost(domain);

  const isBackstageAudience = audience === AUDIENCE_BACKSTAGE;

  if (isBackstageAudience) {
    const backstageSnippet = getBackstageSnippet({
      host,
      issuer,
      publicKeyId,
    });

    return backstageSnippet;
  }

  const investmentsSnippet = getInvestmentsSnippet({
    host,
    issuer,
    publicKeyId,
  });

  return investmentsSnippet;
};

export default getIdTokenSnippet;
